/**
 * Header: Sticky-Schatten, Mega-Menü „Themen“, Header-Suche, mobiles Menü.
 * Immer nur eine Ebene offen. Esc schließt und gibt den Fokus an den Auslöser zurück.
 */
import { linkToButton, trapFocus } from './util.js';

export function initHeader() {
  const header = document.querySelector('.c-header');
  if (!header) return;

  /* Schatten, sobald gescrollt wird */
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  const layers = [];

  /** Registriert einen Auslöser (Fallback-Link) und sein Panel. */
  function register(selector, { onOpen, onClose, modal = false } = {}) {
    const link = header.querySelector(selector);
    if (!link) return null;
    const panel = document.getElementById(link.dataset.controls);
    if (!panel) return null;
    const trigger = linkToButton(link);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', panel.id);

    const layer = {
      trigger,
      panel,
      open() {
        layers.forEach((l) => l !== layer && l.isOpen() && l.close(false));
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (trigger.dataset.labelClose) trigger.setAttribute('aria-label', trigger.dataset.labelClose);
        onOpen?.(layer);
      },
      close(returnFocus = true) {
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        if (trigger.dataset.labelOpen) trigger.setAttribute('aria-label', trigger.dataset.labelOpen);
        onClose?.(layer);
        if (returnFocus) trigger.focus();
      },
      isOpen: () => !panel.hidden,
      modal,
    };
    trigger.addEventListener('click', () => (layer.isOpen() ? layer.close() : layer.open()));
    layers.push(layer);
    return layer;
  }

  /* --- Mega-Menü --- */
  let selectArea = () => {};
  const mega = register('[data-mega-trigger]', {
    onOpen: (l) => {
      const current = l.panel.querySelector('.c-mega__area[data-current]') || l.panel.querySelector('.c-mega__area');
      selectArea(current);
    },
  });
  if (mega) {
    const areas = [...mega.panel.querySelectorAll('.c-mega__area')];
    selectArea = (btn) => {
      areas.forEach((a) => {
        const on = a === btn;
        a.setAttribute('aria-expanded', String(on));
        document.getElementById(a.getAttribute('aria-controls')).hidden = !on;
      });
    };
    let hoverTimer;
    areas.forEach((btn, i) => {
      btn.addEventListener('click', () => selectArea(btn));
      btn.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => selectArea(btn), 120); // kleine Verzögerung gegen Flackern bei diagonaler Mausbewegung
      });
      btn.addEventListener('keydown', (e) => {
        const move = { ArrowDown: 1, ArrowUp: -1 }[e.key];
        if (move) { e.preventDefault(); areas[(i + move + areas.length) % areas.length].focus(); }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          selectArea(btn);
          document.getElementById(btn.getAttribute('aria-controls')).querySelector('.c-mega__pages a')?.focus();
        }
      });
    });
    mega.panel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' && e.target.closest('.c-mega__pages')) {
        e.preventDefault();
        mega.panel.querySelector('.c-mega__area[aria-expanded="true"]')?.focus();
      }
    });
  }

  /* --- Header-Suche --- */
  register('[data-search-trigger]', {
    onOpen: (l) => setTimeout(() => l.panel.querySelector('input')?.focus(), 20),
  });

  /* --- Mobiles Menü --- */
  register('[data-menu-trigger]', {
    modal: true,
    onOpen: (l) => {
      document.documentElement.classList.add('has-open-menu');
      const current = l.panel.querySelector('[aria-current="page"]');
      current?.closest('details')?.setAttribute('open', '');
      l.panel.querySelector('summary, a')?.focus();
    },
    onClose: () => document.documentElement.classList.remove('has-open-menu'),
  });

  /* Gemeinsame Tastatur- und Klick-Regeln */
  document.addEventListener('keydown', (e) => {
    const open = layers.find((l) => l.isOpen());
    if (!open) return;
    if (e.key === 'Escape') { open.close(); return; }
    if (open.modal) trapFocus([header, open.panel], e);
  });
  document.addEventListener('click', (e) => {
    const open = layers.find((l) => l.isOpen());
    if (open && !open.modal && !header.contains(e.target)) open.close(false);
  });
  // Mobiles Menü schließen, wenn das Fenster auf Desktop-Breite wächst
  window.matchMedia('(min-width: 64em)').addEventListener('change', (mq) => {
    const menu = layers.find((l) => l.modal);
    if (mq.matches && menu?.isOpen()) menu.close(false);
  });
}
